import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ModalName } from '../../../../enums';
import { useSendEmail } from '../../../../features/app';
import { useCloseModal } from '../../../../features/modals';
import { areValuesValid } from '../../../../utils';
import { Button, FormFields, Input, Modal } from '../../../common';
import { ISendFeedbackModalBodyProps } from './types';

export const SendFeedbackModal = () => {
  const { t } = useTranslation();
  const [body, setBody] = useState<ISendFeedbackModalBodyProps>({
    name: '',
    email: '',
    message: '',
  });
  const closeModal = useCloseModal(ModalName.SEND_FEEDBACK);
  const sendEmail = useSendEmail();

  const isValid = useMemo(
    () => areValuesValid([body.name, body.message, { type: 'email', value: body.email }]),
    [body],
  );

  const setName = (name: string) => setBody({ ...body, name });
  const setEmail = (email: string) => setBody({ ...body, email });
  const setMessage = (message: string) => setBody({ ...body, message });

  const send = useCallback(() => {
    const bodyString = `
      Name: ${body.name} <br>
      Email: ${body.email} <br>
      Message: ${body.message}
    `;
    const params = {
      subject: 'Feedback Submitted',
      body: bodyString,
    };
    sendEmail(params);
    closeModal();
  }, [sendEmail, closeModal, body]);

  const footer: JSX.Element = useMemo(() => {
    return (
      <div className="flex">
        <Button text={t('modal.feedback.button.send')} disabled={!isValid} onClick={send} />
        <Button text={t('modal.feedback.button.cancel')} type="tertiary" onClick={closeModal} className="ml-2" />
      </div>
    );
  }, [closeModal, send, isValid]);

  return (
    <Modal name={ModalName.SEND_FEEDBACK} header={t('modal.feedback.button.send')} footer={footer}>
      <FormFields>
        <Input
          value={body.name}
          label={t('modal.feedback.field.name.label')}
          onChange={setName}
          placeholder={t('modal.feedback.field.name.placeholder')}
        />

        <Input
          value={body.email}
          label={t('modal.feedback.field.email.label')}
          onChange={setEmail}
          placeholder={t('modal.feedback.field.email.placeholder')}
          type="email"
        />

        <Input
          value={body.message}
          label={t('modal.feedback.field.message.label')}
          onChange={setMessage}
          placeholder={t('modal.feedback.field.message.placeholder')}
          type="textarea"
        />
      </FormFields>
    </Modal>
  );
};
